import { COLORS } from "../shared/constants";
import { IconBack, IconCamera, IconAlert, IconCheck, IconEdit } from "../shared/icons";
import { useState, useRef } from "react";
import * as api from "../api";

export default // ─── Patient Form (Create / Edit) ───────────────────────────
function PatientForm({ patient, providers, diagnoses, onSave, onCancel, onAddProvider, onEditProvider, onDeleteProvider }) {
  const isEdit = !!patient;
  const dobRef = useRef(null);
  const [form, setForm] = useState({
    first_name: patient?.first_name || "",
    last_name: patient?.last_name || "",
    date_of_birth: patient?.date_of_birth || "",
    gender: patient?.gender || "",
    diagnosis_code: patient?.diagnosis_code || "",
    referring_provider_id: patient?.referring_provider_id || "",
    referring_provider_text: "",
    precautions: patient?.precautions || "",
  });
  const [providerMode, setProviderMode] = useState("dropdown"); // dropdown | text
  const [editingProvider, setEditingProvider] = useState(null); // null or provider object being edited
  const [scanning, setScanning] = useState(false);
  const scanFileRef = useRef(null);
  const [errors, setErrors] = useState({});

  const handleScanReferral = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    try {
      const result = await api.ocrScanReferral(file);
      // Pre-fill the provider text fields and switch to text mode
      setForm((f) => ({
        ...f,
        prov_name: result.name || "",
        prov_practice: result.practice_name || "",
        prov_phone: result.phone || "",
        prov_fax: result.fax || "",
        prov_email: result.email || "",
        prov_addr1: result.address_line1 || "",
        prov_addr2: result.address_line2 || "",
        prov_city: result.city || "",
        prov_state: result.state || "",
        prov_zip: result.zip || "",
      }));
      setProviderMode("text");
    } catch (err) {
      alert(err.error || "OCR scan failed.");
    }
    setScanning(false);
    if (scanFileRef.current) scanFileRef.current.value = "";
  };

  const handleChange = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: null }));
  };

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = "Required";
    if (!form.last_name.trim()) errs.last_name = "Required";
    const dobVal = dobRef.current ? dobRef.current.value : form.date_of_birth;
    if (!dobVal) errs.date_of_birth = "Required";
    if (!form.gender) errs.gender = "Required";
    if (!form.diagnosis_code) errs.diagnosis_code = "Required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const dobVal = dobRef.current ? dobRef.current.value : form.date_of_birth;
    const data = { ...patient, ...form, date_of_birth: dobVal };
    if (providerMode === "text" && (form.prov_name || "").trim()) {
      // Provider was typed in but not saved to list — store as freetext reference
      if (!form.referring_provider_id) {
        data._referring_provider_freetext = form.prov_name.trim();
      }
    }
    onSave(data);
  };

  const inputStyle = (field) => ({
    width: "100%",
    height: "44px",
    padding: "0 14px",
    borderRadius: "8px",
    border: `1.5px solid ${errors[field] ? COLORS.danger : COLORS.border}`,
    background: COLORS.inputBg,
    fontSize: "14px",
    color: COLORS.text,
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s",
  });

  const labelStyle = {
    fontSize: "13px",
    fontWeight: "600",
    color: COLORS.textSecondary,
    marginBottom: "6px",
    display: "block",
  };

  return (
    <div style={{ padding: "24px", maxWidth: "680px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "28px" }}>
        <button
          onClick={onCancel}
          style={{
            background: "none",
            border: `1px solid ${COLORS.border}`,
            borderRadius: "8px",
            padding: "8px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            color: COLORS.textSecondary,
          }}
        >
          <IconBack />
        </button>
        <h2 style={{ color: COLORS.text, fontSize: "20px", fontWeight: "600", margin: 0 }}>
          {isEdit ? "Edit Patient" : "New Patient"}
        </h2>
        {isEdit && (
          <span style={{ color: COLORS.textSecondary, fontSize: "14px" }}>
            #{patient.patient_number}
          </span>
        )}
      </div>

      {/* Precautions banner (edit mode) */}
      {isEdit && patient.precautions && (
        <div style={{
          background: COLORS.dangerLight,
          border: `1px solid ${COLORS.dangerBorder}`,
          borderRadius: "10px",
          padding: "12px 16px",
          marginBottom: "24px",
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
        }}>
          <span style={{ color: COLORS.danger, flexShrink: 0, marginTop: "1px" }}><IconAlert /></span>
          <div>
            <span style={{ fontSize: "12px", fontWeight: "700", color: COLORS.danger, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Precautions
            </span>
            <p style={{ margin: "4px 0 0", fontSize: "14px", color: COLORS.text, lineHeight: "1.5" }}>
              {patient.precautions}
            </p>
          </div>
        </div>
      )}

      <div style={{
        background: COLORS.surface,
        borderRadius: "12px",
        border: `1px solid ${COLORS.borderLight}`,
        padding: "28px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      }}>
        {/* Name row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>
          <div>
            <label style={labelStyle}>First Name *</label>
            <input
              style={inputStyle("first_name")}
              value={form.first_name}
              onChange={(e) => handleChange("first_name", e.target.value)}
              placeholder="First name"
              onFocus={(e) => (e.target.style.borderColor = COLORS.primary)}
              onBlur={(e) => (e.target.style.borderColor = errors.first_name ? COLORS.danger : COLORS.border)}
            />
            {errors.first_name && <span style={{ fontSize: "12px", color: COLORS.danger }}>{errors.first_name}</span>}
          </div>
          <div>
            <label style={labelStyle}>Last Name *</label>
            <input
              style={inputStyle("last_name")}
              value={form.last_name}
              onChange={(e) => handleChange("last_name", e.target.value)}
              placeholder="Last name"
              onFocus={(e) => (e.target.style.borderColor = COLORS.primary)}
              onBlur={(e) => (e.target.style.borderColor = errors.last_name ? COLORS.danger : COLORS.border)}
            />
            {errors.last_name && <span style={{ fontSize: "12px", color: COLORS.danger }}>{errors.last_name}</span>}
          </div>
        </div>

        {/* DOB + Gender row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>
          <div>
            <label style={labelStyle}>Date of Birth *</label>
            <input
              ref={dobRef}
              type="date"
              min="1900-01-01"
              max={new Date().toISOString().split("T")[0]}
              style={inputStyle("date_of_birth")}
              defaultValue={form.date_of_birth}
              onFocus={(e) => (e.target.style.borderColor = COLORS.primary)}
              onBlur={(e) => (e.target.style.borderColor = errors.date_of_birth ? COLORS.danger : COLORS.border)}
            />
            {errors.date_of_birth && <span style={{ fontSize: "12px", color: COLORS.danger }}>{errors.date_of_birth}</span>}
          </div>
          <div>
            <label style={labelStyle}>Gender *</label>
            <select
              style={inputStyle("gender")}
              value={form.gender}
              onChange={(e) => handleChange("gender", e.target.value)}
            >
              <option value="">Select…</option>
              <option value="MALE">Male</option>
              <option value="FEMALE">Female</option>
              <option value="OTHER">Other</option>
              <option value="UNSPECIFIED">Unspecified</option>
            </select>
            {errors.gender && <span style={{ fontSize: "12px", color: COLORS.danger }}>{errors.gender}</span>}
          </div>
        </div>

        {/* Diagnosis */}
        <div style={{ marginBottom: "20px" }}>
          <label style={labelStyle}>Diagnosis / ICD-10 *</label>
          <select
            style={inputStyle("diagnosis_code")}
            value={form.diagnosis_code}
            onChange={(e) => handleChange("diagnosis_code", e.target.value)}
          >
            <option value="">Select diagnosis…</option>
            {diagnoses.map((d) => (
              <option key={d.code} value={d.code}>
                {d.code} — {d.description}
              </option>
            ))}
          </select>
          {errors.diagnosis_code && <span style={{ fontSize: "12px", color: COLORS.danger }}>{errors.diagnosis_code}</span>}
        </div>

        {/* Referring Provider */}
        <div style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>Referring Provider</label>
            <div style={{ display: "flex", gap: "4px" }}>
              <button
                onClick={() => setProviderMode("dropdown")}
                style={{
                  fontSize: "11px",
                  padding: "3px 10px",
                  borderRadius: "6px",
                  border: `1px solid ${COLORS.border}`,
                  background: providerMode === "dropdown" ? COLORS.primary : "transparent",
                  color: providerMode === "dropdown" ? "#FFF" : COLORS.textSecondary,
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Saved List
              </button>
              <button
                onClick={() => setProviderMode("text")}
                style={{
                  fontSize: "11px",
                  padding: "3px 10px",
                  borderRadius: "6px",
                  border: `1px solid ${COLORS.border}`,
                  background: providerMode === "text" ? COLORS.primary : "transparent",
                  color: providerMode === "text" ? "#FFF" : COLORS.textSecondary,
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Type In
              </button>
              <button
                onClick={() => scanFileRef.current?.click()}
                disabled={scanning}
                style={{
                  fontSize: "11px",
                  padding: "3px 10px",
                  borderRadius: "6px",
                  border: `1px solid ${COLORS.border}`,
                  background: "transparent",
                  color: COLORS.textSecondary,
                  cursor: scanning ? "wait" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontWeight: "600",
                  opacity: scanning ? 0.7 : 1,
                }}
                title="Scan referral via camera/OCR"
              >
                <IconCamera /> {scanning ? "Scanning..." : "Scan"}
              </button>
              <input ref={scanFileRef} type="file" accept="image/*" capture="environment" onChange={handleScanReferral} style={{ display: "none" }} />
            </div>
          </div>

          {providerMode === "dropdown" ? (
            <div>
              <select
                style={{ ...inputStyle(), borderColor: COLORS.border }}
                value={form.referring_provider_id}
                onChange={(e) => handleChange("referring_provider_id", e.target.value)}
              >
                <option value="">None selected</option>
                {providers.map((pv) => (
                  <option key={pv.id} value={pv.id}>
                    {pv.name}{pv.practice_name ? ` — ${pv.practice_name}` : ""}
                  </option>
                ))}
              </select>
              {/* Show selected provider details with edit/delete */}
              {form.referring_provider_id && (() => {
                const pv = providers.find((p) => p.id === form.referring_provider_id);
                if (!pv) return null;

                // Inline edit mode
                if (editingProvider && editingProvider.id === pv.id) {
                  const ep = editingProvider;
                  const epChange = (field, val) => setEditingProvider((prev) => ({ ...prev, [field]: val }));
                  const epInput = (field, placeholder, width) => (
                    <input
                      style={{ height: "34px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "12px", background: COLORS.surface, width: width || "100%", boxSizing: "border-box" }}
                      value={ep[field] || ""}
                      onChange={(e) => epChange(field, e.target.value)}
                      placeholder={placeholder}
                    />
                  );
                  return (
                    <div style={{
                      marginTop: "10px",
                      padding: "14px",
                      background: COLORS.primaryLight,
                      borderRadius: "8px",
                      border: `1px solid ${COLORS.primary}40`,
                    }}>
                      <div style={{ fontSize: "12px", fontWeight: "700", color: COLORS.primary, marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Edit Provider
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "8px" }}>
                        <div><label style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary }}>Provider Name</label>{epInput("name", "Name")}</div>
                        <div><label style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary }}>Practice / Clinic</label>{epInput("practice_name", "Practice")}</div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginBottom: "8px" }}>
                        <div><label style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary }}>Phone</label>{epInput("phone", "(xxx) xxx-xxxx")}</div>
                        <div><label style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary }}>Fax</label>{epInput("fax", "(xxx) xxx-xxxx")}</div>
                        <div><label style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary }}>Email</label>{epInput("email", "email@example.com")}</div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "8px" }}>
                        <div><label style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary }}>Address Line 1</label>{epInput("address_line1", "Street")}</div>
                        <div><label style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary }}>Address Line 2</label>{epInput("address_line2", "Suite, etc.")}</div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "8px", marginBottom: "12px" }}>
                        <div><label style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary }}>City</label>{epInput("city", "City")}</div>
                        <div><label style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary }}>State</label>{epInput("state", "SC")}</div>
                        <div><label style={{ fontSize: "10px", fontWeight: "600", color: COLORS.textSecondary }}>ZIP</label>{epInput("zip", "29483")}</div>
                      </div>
                      <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                        <button
                          onClick={() => setEditingProvider(null)}
                          style={{ height: "32px", padding: "0 14px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, fontSize: "12px", fontWeight: "500", cursor: "pointer" }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => {
                            onEditProvider(editingProvider);
                            setEditingProvider(null);
                          }}
                          style={{ height: "32px", padding: "0 14px", borderRadius: "6px", border: "none", background: COLORS.primary, color: "#FFF", fontSize: "12px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}
                        >
                          <IconCheck /> Save
                        </button>
                      </div>
                    </div>
                  );
                }

                // Read-only detail card with edit/delete
                return (
                  <div style={{
                    marginTop: "10px",
                    padding: "12px 14px",
                    background: COLORS.inputBg,
                    borderRadius: "8px",
                    border: `1px solid ${COLORS.borderLight}`,
                    fontSize: "13px",
                    color: COLORS.textSecondary,
                    lineHeight: "1.6",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontWeight: "600", color: COLORS.text, marginBottom: "4px" }}>{pv.name}</div>
                        {pv.practice_name && <div>{pv.practice_name}</div>}
                        {pv.address_line1 && <div>{pv.address_line1}</div>}
                        {pv.address_line2 && <div>{pv.address_line2}</div>}
                        {(pv.city || pv.state || pv.zip) && <div>{[pv.city, pv.state].filter(Boolean).join(", ")}{pv.zip ? ` ${pv.zip}` : ""}</div>}
                        {(pv.phone || pv.fax) && (
                          <div style={{ marginTop: "4px" }}>
                            {pv.phone && <span>Ph: {pv.phone}</span>}
                            {pv.phone && pv.fax && <span style={{ margin: "0 8px" }}>·</span>}
                            {pv.fax && <span>Fax: {pv.fax}</span>}
                          </div>
                        )}
                        {pv.email && <div>{pv.email}</div>}
                      </div>
                      <div style={{ display: "flex", gap: "4px", flexShrink: 0, marginLeft: "12px" }}>
                        <button
                          onClick={() => setEditingProvider({ ...pv })}
                          style={{
                            height: "28px",
                            padding: "0 10px",
                            borderRadius: "6px",
                            border: `1px solid ${COLORS.border}`,
                            background: COLORS.surface,
                            color: COLORS.primary,
                            fontSize: "11px",
                            fontWeight: "600",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <IconEdit /> Edit
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(`Remove ${pv.name} from the provider list?`)) {
                              onDeleteProvider(pv.id);
                              handleChange("referring_provider_id", "");
                            }
                          }}
                          style={{
                            height: "28px",
                            padding: "0 10px",
                            borderRadius: "6px",
                            border: `1px solid ${COLORS.dangerBorder}`,
                            background: COLORS.dangerLight,
                            color: COLORS.danger,
                            fontSize: "11px",
                            fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                <div>
                  <label style={{ ...labelStyle, fontSize: "11px" }}>Provider Name</label>
                  <input
                    style={{ ...inputStyle(), borderColor: COLORS.border }}
                    value={form.prov_name || ""}
                    onChange={(e) => handleChange("prov_name", e.target.value)}
                    placeholder="Dr. Name"
                    onFocus={(e) => (e.target.style.borderColor = COLORS.primary)}
                    onBlur={(e) => (e.target.style.borderColor = COLORS.border)}
                  />
                </div>
                <div>
                  <label style={{ ...labelStyle, fontSize: "11px" }}>Practice / Clinic Name</label>
                  <input
                    style={{ ...inputStyle(), borderColor: COLORS.border }}
                    value={form.prov_practice || ""}
                    onChange={(e) => handleChange("prov_practice", e.target.value)}
                    placeholder="Clinic name"
                    onFocus={(e) => (e.target.style.borderColor = COLORS.primary)}
                    onBlur={(e) => (e.target.style.borderColor = COLORS.border)}
                  />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                <div>
                  <label style={{ ...labelStyle, fontSize: "11px" }}>Phone</label>
                  <input
                    style={{ ...inputStyle(), borderColor: COLORS.border }}
                    value={form.prov_phone || ""}
                    onChange={(e) => handleChange("prov_phone", e.target.value)}
                    placeholder="(xxx) xxx-xxxx"
                    onFocus={(e) => (e.target.style.borderColor = COLORS.primary)}
                    onBlur={(e) => (e.target.style.borderColor = COLORS.border)}
                  />
                </div>
                <div>
                  <label style={{ ...labelStyle, fontSize: "11px" }}>Fax</label>
                  <input
                    style={{ ...inputStyle(), borderColor: COLORS.border }}
                    value={form.prov_fax || ""}
                    onChange={(e) => handleChange("prov_fax", e.target.value)}
                    placeholder="(xxx) xxx-xxxx"
                    onFocus={(e) => (e.target.style.borderColor = COLORS.primary)}
                    onBlur={(e) => (e.target.style.borderColor = COLORS.border)}
                  />
                </div>
                <div>
                  <label style={{ ...labelStyle, fontSize: "11px" }}>Email</label>
                  <input
                    style={{ ...inputStyle(), borderColor: COLORS.border }}
                    value={form.prov_email || ""}
                    onChange={(e) => handleChange("prov_email", e.target.value)}
                    placeholder="email@example.com"
                    onFocus={(e) => (e.target.style.borderColor = COLORS.primary)}
                    onBlur={(e) => (e.target.style.borderColor = COLORS.border)}
                  />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                <div>
                  <label style={{ ...labelStyle, fontSize: "11px" }}>Address Line 1</label>
                  <input
                    style={{ ...inputStyle(), borderColor: COLORS.border }}
                    value={form.prov_addr1 || ""}
                    onChange={(e) => handleChange("prov_addr1", e.target.value)}
                    placeholder="Street address"
                    onFocus={(e) => (e.target.style.borderColor = COLORS.primary)}
                    onBlur={(e) => (e.target.style.borderColor = COLORS.border)}
                  />
                </div>
                <div>
                  <label style={{ ...labelStyle, fontSize: "11px" }}>Address Line 2</label>
                  <input
                    style={{ ...inputStyle(), borderColor: COLORS.border }}
                    value={form.prov_addr2 || ""}
                    onChange={(e) => handleChange("prov_addr2", e.target.value)}
                    placeholder="Suite, unit, etc."
                    onFocus={(e) => (e.target.style.borderColor = COLORS.primary)}
                    onBlur={(e) => (e.target.style.borderColor = COLORS.border)}
                  />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                <div>
                  <label style={{ ...labelStyle, fontSize: "11px" }}>City</label>
                  <input
                    style={{ ...inputStyle(), borderColor: COLORS.border }}
                    value={form.prov_city || ""}
                    onChange={(e) => handleChange("prov_city", e.target.value)}
                    placeholder="City"
                    onFocus={(e) => (e.target.style.borderColor = COLORS.primary)}
                    onBlur={(e) => (e.target.style.borderColor = COLORS.border)}
                  />
                </div>
                <div>
                  <label style={{ ...labelStyle, fontSize: "11px" }}>State</label>
                  <input
                    style={{ ...inputStyle(), borderColor: COLORS.border }}
                    value={form.prov_state || ""}
                    onChange={(e) => handleChange("prov_state", e.target.value)}
                    placeholder="SC"
                    maxLength={2}
                    onFocus={(e) => (e.target.style.borderColor = COLORS.primary)}
                    onBlur={(e) => (e.target.style.borderColor = COLORS.border)}
                  />
                </div>
                <div>
                  <label style={{ ...labelStyle, fontSize: "11px" }}>ZIP</label>
                  <input
                    style={{ ...inputStyle(), borderColor: COLORS.border }}
                    value={form.prov_zip || ""}
                    onChange={(e) => handleChange("prov_zip", e.target.value)}
                    placeholder="29483"
                    maxLength={10}
                    onFocus={(e) => (e.target.style.borderColor = COLORS.primary)}
                    onBlur={(e) => (e.target.style.borderColor = COLORS.border)}
                  />
                </div>
              </div>

              {/* Save to provider list */}
              {(form.prov_name || "").trim() && (
                <div style={{
                  padding: "10px 14px",
                  background: COLORS.primaryLight,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}>
                  <span style={{ fontSize: "12px", color: COLORS.primary, fontWeight: "500" }}>
                    Save this provider to the system-wide list?
                  </span>
                  <button
                    onClick={() => {
                      const name = (form.prov_name || "").trim();
                      if (name) {
                        const newProv = onAddProvider({
                          name,
                          practice_name: form.prov_practice || "",
                          phone: form.prov_phone || "",
                          fax: form.prov_fax || "",
                          email: form.prov_email || "",
                          address_line1: form.prov_addr1 || "",
                          address_line2: form.prov_addr2 || "",
                          city: form.prov_city || "",
                          state: form.prov_state || "",
                          zip: form.prov_zip || "",
                        });
                        handleChange("referring_provider_id", newProv.id);
                        setProviderMode("dropdown");
                      }
                    }}
                    style={{
                      height: "32px",
                      padding: "0 14px",
                      borderRadius: "6px",
                      border: "none",
                      background: COLORS.accent,
                      color: "#FFF",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <IconCheck /> Save to List
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Precautions */}
        <div style={{ marginBottom: "28px" }}>
          <label style={labelStyle}>Precautions</label>
          <textarea
            style={{
              width: "100%",
              minHeight: "80px",
              padding: "12px 14px",
              borderRadius: "8px",
              border: `1.5px solid ${COLORS.border}`,
              background: COLORS.inputBg,
              fontSize: "14px",
              color: COLORS.text,
              outline: "none",
              resize: "vertical",
              boxSizing: "border-box",
              fontFamily: "inherit",
            }}
            value={form.precautions}
            onChange={(e) => handleChange("precautions", e.target.value)}
            placeholder="Enter any safety precautions or alerts (will display as a persistent banner)…"
            onFocus={(e) => (e.target.style.borderColor = COLORS.primary)}
            onBlur={(e) => (e.target.style.borderColor = COLORS.border)}
          />
          {form.precautions && (
            <div style={{
              marginTop: "8px",
              background: COLORS.warningLight,
              border: `1px solid ${COLORS.warningBorder}`,
              borderRadius: "8px",
              padding: "8px 12px",
              fontSize: "12px",
              color: COLORS.warning,
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}>
              <IconAlert /> This will display as a persistent alert during treatment
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
          <button
            onClick={onCancel}
            style={{
              height: "44px",
              padding: "0 24px",
              borderRadius: "8px",
              border: `1px solid ${COLORS.border}`,
              background: "transparent",
              color: COLORS.textSecondary,
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            style={{
              height: "44px",
              padding: "0 28px",
              borderRadius: "8px",
              border: "none",
              background: COLORS.primary,
              color: "#FFF",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = COLORS.primaryHover)}
            onMouseLeave={(e) => (e.currentTarget.style.background = COLORS.primary)}
          >
            {isEdit ? "Save Changes" : "Create Patient"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Protocol List ───────────────────────────────────────────

